/** 用户端 API：关系合盘 / 流年深度 / 大运 / 每日运势 / 优惠券 / 邀请 */
import { api, postStream } from './api';

// ============================================================
// 关系合盘
// ============================================================
export interface CompatibilityReportSummary {
  id: string;
  chartIdA: string;
  chartIdB: string;
  nameA: string | null;
  nameB: string | null;
  relation: 'couple' | 'partner' | 'family' | 'friend';
  status: 'pending' | 'streaming' | 'completed' | 'failed';
  content: string | null;
  createdAt: string;
}

export const compatibilityApi = {
  listMine: () => api.get<CompatibilityReportSummary[]>('/compatibility'),
  detail: (id: string) => api.get<CompatibilityReportSummary>(`/compatibility/${id}`),
  stream: (
    body: {
      chartIdA: string;
      chartIdB?: string;
      partner?: {
        date: string;
        time?: string;
        gender: 'male' | 'female' | 'unknown';
        birthplace?: string;
      };
      nameA?: string;
      nameB?: string;
      relation?: 'couple' | 'partner' | 'family' | 'friend';
      overrideModelCode?: string;
    },
    handlers: {
      onDelta: (text: string) => void;
      onDone: (reportId?: string) => void;
      onError?: (err: Error) => void;
    },
    signal?: AbortSignal,
  ) =>
    postStream(
      '/compatibility/stream',
      body,
      {
        onEvent: (event, data) => {
          if (event === 'done') {
            const id = (data as { reportId?: string } | null)?.reportId;
            handlers.onDone(id);
          } else if (event === 'message') {
            const delta = (data as { delta?: string } | null)?.delta;
            if (typeof delta === 'string') handlers.onDelta(delta);
          } else if (event === 'error') {
            const message = (data as { message?: string } | null)?.message ?? '生成失败';
            handlers.onError?.(new Error(message));
          }
        },
        onError: handlers.onError,
      },
      { signal },
    ),
};

// ============================================================
// 流年 / 大运
// ============================================================
export interface FortuneYearReport {
  id: string;
  chartId: string;
  targetYear: number;
  status: 'pending' | 'streaming' | 'completed' | 'failed';
  content: string | null;
  sections: unknown;
}

export interface DayunReport {
  id: string;
  chartId: string;
  dayunIndex: number;
  startYear: number;
  startAge: number;
  ganZhi: string;
  status: 'pending' | 'streaming' | 'completed' | 'failed';
  content: string | null;
}

const sseHandlers = (handlers: {
  onDelta: (text: string) => void;
  onDone: (reportId?: string) => void;
  onError?: (err: Error) => void;
}) => ({
  onEvent: (event: string, data: unknown) => {
    if (event === 'done') {
      handlers.onDone((data as { reportId?: string } | null)?.reportId);
    } else if (event === 'message') {
      const delta = (data as { delta?: string } | null)?.delta;
      if (typeof delta === 'string') handlers.onDelta(delta);
    } else if (event === 'error') {
      handlers.onError?.(new Error((data as { message?: string } | null)?.message ?? '生成失败'));
    }
  },
  onError: handlers.onError,
});

export const fortuneApi = {
  yearStream: (
    body: { chartId: string; targetYear: number; overrideModelCode?: string },
    handlers: {
      onDelta: (s: string) => void;
      onDone: (id?: string) => void;
      onError?: (e: Error) => void;
    },
    signal?: AbortSignal,
  ) => postStream('/fortune/year/stream', body, sseHandlers(handlers), { signal }),
  listYears: () => api.get<FortuneYearReport[]>('/fortune/year'),
  yearDetail: (id: string) => api.get<FortuneYearReport>(`/fortune/year/${id}`),
  dayunStream: (
    body: { chartId: string; dayunIndex: number; overrideModelCode?: string },
    handlers: {
      onDelta: (s: string) => void;
      onDone: (id?: string) => void;
      onError?: (e: Error) => void;
    },
    signal?: AbortSignal,
  ) => postStream('/fortune/dayun/stream', body, sseHandlers(handlers), { signal }),
  listDayun: () => api.get<DayunReport[]>('/fortune/dayun'),
  dayunDetail: (id: string) => api.get<DayunReport>(`/fortune/dayun/${id}`),
};

// ============================================================
// 每日运势 + 推送订阅
// ============================================================
export interface DailyFortune {
  id: string;
  chartId: string;
  date: string;
  dayGanZhi: string | null;
  jieqiName: string | null;
  scores: Record<string, number> | null;
  content: {
    oneliner: string;
    favor: string[];
    avoid: string[];
    action: string;
    taboo?: string;
  } | null;
}

export interface PushSubscription {
  id: string;
  emailEnabled: boolean;
  pushHour: number;
  primaryChartId: string | null;
  unsubscribeToken: string;
  lastPushedDate: string | null;
}

export const dailyFortuneApi = {
  today: (chartId?: string, date?: string) => {
    const q = new URLSearchParams();
    if (chartId) q.set('chartId', chartId);
    if (date) q.set('date', date);
    const path = q.toString() ? `/daily-fortune?${q}` : '/daily-fortune';
    return api.get<DailyFortune>(path);
  },
  getSubscription: () => api.get<PushSubscription>('/daily-fortune/subscription'),
  setSubscription: (patch: {
    emailEnabled?: boolean;
    pushHour?: number;
    primaryChartId?: string | null;
  }) => api.put<PushSubscription>('/daily-fortune/subscription', patch),
};

// ============================================================
// 优惠券
// ============================================================
export interface UserCoupon {
  id: string;
  couponCode: string;
  status: 'unused' | 'used' | 'expired' | 'revoked';
  expireAt: string | null;
  usedAt: string | null;
  coupon: {
    name: string;
    discountKind: 'percent_off' | 'amount_off' | 'fixed_price';
    value: number;
    minAmountYuan: number | null;
    scopeProductCodes: string[];
  };
}

export const couponApi = {
  listMine: (status?: 'unused' | 'used' | 'expired' | 'revoked') =>
    api.get<UserCoupon[]>(`/coupons${status ? `?status=${status}` : ''}`),
  claim: (couponCode: string) => api.post<UserCoupon>('/coupons/claim', { couponCode }),
};

// ============================================================
// 邀请
// ============================================================
export interface ReferralStats {
  total: number;
  rewarded: number;
  qualified: number;
  pending: number;
}

export const referralApi = {
  me: () => api.get<{ inviteCode: string; stats: ReferralStats }>('/referral/me'),
  list: () =>
    api.get<
      Array<{
        id: string;
        status: 'pending' | 'qualified' | 'rewarded' | 'rejected';
        createdAt: string;
        invitee: { id: string; email: string; name: string | null; createdAt: string };
      }>
    >('/referral/list'),
  lookupInviter: (code: string) =>
    api.get<{ name: string; avatarUrl: string | null } | null>(
      `/referral/lookup?code=${encodeURIComponent(code)}`,
      { auth: false },
    ),
};
